// @ts-strict-ignore
import { SetupStepType } from './../typescriptHelpers';
// Local storage keys

import { AutomationType, IntegrationType, ReleaseNav, ReleaseStepType } from '../types';
import { OrgSummaryPreferences } from '../types/summaryView';
import { StringKeyedDictionary } from '../typescriptHelpers';

export const REMEMBER_ME_STORAGE_KEY = 'user-email';
export const REMEMBER_ME_SSO_STORAGE_KEY = 'sso-user-email';
export const DEFAULT_APP_IN_ORG = 'default-app-in-org';
export const DEFAULT_APP_IN_APP_GROUP = 'default-app-in-app-group';
export const LAST_VIEWED_RELEASE_STEP_FOR_RELEASE = 'last-viewed-release-step-for-release';
export const USER_HAS_VIEWED_ONBOARDING = 'user-has-viewed-onboarding';
export const LAST_LOGIN = 'last-login';
export const SETUP_STEPS_DATA = 'setup-steps-data';
export const SEEN_NEW_AUTOMATIONS = 'seen-new-automations';
export const SEEN_UPDATED_AUTOMATIONS = 'seen-updated-automations';
export const GET_NOTIFIED_COMING_SOON_INTEGRATIONS = 'get-notified-coming-soon-integrations';
export const QUALITATIVE_ONBOARDING_DISMISSED = 'qualitative-onboarding-dismissed';
export const REGISTER_DEVICE_DISMISSED = 'register-device-dismissed';
export const RELEASE_FEEDBACK = 'release-feedback';
export const FR_ITEMS_FILTERS = 'fr-items-filters';
export const FR_ITEMS_SORT = 'fr-items-sort';
export const FT_ITEMS_ONLY_FIXES = 'ft-items-only-fixes';
export const FT_ITEMS_GROUP_BY_TICKET = 'ft-items-group-by-ticket';
export const FT_ITEMS_GROUP_BY_EPIC = 'ft-items-group-by-epic';
export const AUTH_REDIRECT_ROUTE = 'auth-redirect-route';
export const APP_SUMMARY_VIEW_PREFERENCES = 'app-summary-view-preferences';
export const ORG_SUMMARY_VIEW_PREFERENCES = 'org-summary-view-preferences';
export const IOS_DEPENDENCIES_TAB_VISITED = 'ios-dependencies-tab-visited';
export const TRIAGE_TAB_VISITED = 'triage-tab-visited';
/* return string-keyed dictionary of releaseId: fingerprint of the attention items seen on the timeline */
export const SEEN_TIMELINE_ATTENTION = 'seen-timeline-attention';
/* return string-keyed dictionary of appId or releaseId: ISO time the triage page was last seen */
export const SEEN_TRIAGE_AT = 'seen-triage-at';
/* return string-keyed dictionary of appId: JSON array of the dependency updates seen on any of its releases' dependencies page */
export const SEEN_DEPENDENCIES_UPDATES = 'seen-dependencies-updates';
/* return string-keyed dictionary of userId: orgId for default org */
export const DEFAULT_ORG_FOR_USER = 'default-org-for-user';

/* Getters and settings for local storage */

export const setItem: (key: string, value: unknown, storage?: Storage) => void = (
    key,
    value,
    storage = window.localStorage,
) => {
    storage?.setItem(key, JSON.stringify(value));
};

export const deleteItem: (key: string, storage?: Storage) => void = (key, storage = window.localStorage) => {
    storage?.removeItem(key);
};

export const getItem: <T>(key: string, storage?: Storage) => T = (key, storage = window.localStorage) => {
    const value = storage?.getItem(key);

    if (value) {
        return JSON.parse(value);
    }

    return null;
};

/* helpers */

export const getDefaultOrgForUser: (userId: string) => string = (userId) => {
    const data: StringKeyedDictionary<string> = getItem(DEFAULT_ORG_FOR_USER);

    return data ? data[userId] : null;
};

export const getLastSelectedReleaseStepForRelease: (releaseId: string) => ReleaseStepType | ReleaseNav = (
    releaseId,
) => {
    const lastSelectedReleaseStepForRelease: StringKeyedDictionary<string> = getItem(
        LAST_VIEWED_RELEASE_STEP_FOR_RELEASE,
    );

    const releaseStep = lastSelectedReleaseStepForRelease ? lastSelectedReleaseStepForRelease[releaseId] : null;

    return releaseStep as ReleaseStepType | ReleaseNav;
};

export const clearLastSelectedReleaseStepForRelease: (releaseId: string) => void = (releaseId) => {
    const lastSelectedReleaseStepForRelease: StringKeyedDictionary<string> = getItem(
        LAST_VIEWED_RELEASE_STEP_FOR_RELEASE,
    );

    lastSelectedReleaseStepForRelease[releaseId] = null;

    setItem(LAST_VIEWED_RELEASE_STEP_FOR_RELEASE, lastSelectedReleaseStepForRelease);
};

export const getDefaultAppIdInGroupId: (groupId: string) => string = (groupId) => {
    const data: StringKeyedDictionary<string> = getItem(DEFAULT_APP_IN_APP_GROUP);

    return data ? data[groupId] : null;
};

export const setDefaultAppIdParamInGroupId: (groupId: string, appIdParam: string) => void = (groupId, appIdParam) => {
    const data: StringKeyedDictionary<string> = getItem(DEFAULT_APP_IN_APP_GROUP);

    const updatedData = {
        ...data,
        [groupId]: appIdParam,
    };

    setItem(DEFAULT_APP_IN_APP_GROUP, updatedData);
};

export const getDefaultAppIdParamInOrg: (orgId: string) => string = (orgId) => {
    const data: StringKeyedDictionary<string> = getItem(DEFAULT_APP_IN_ORG);

    return data ? data[orgId] : null;
};

export const setDefaultAppIdParamInOrg: (orgId: string, appIdParam: string) => void = (orgId, appIdParam) => {
    const data = getItem(DEFAULT_APP_IN_ORG);

    const dataType = typeof data;
    let updatedData = {};

    if (dataType === 'string') {
        updatedData[orgId] = appIdParam;
    } else {
        const objectData = data as StringKeyedDictionary<string>;

        updatedData = {
            ...objectData,
            [orgId]: appIdParam,
        };
    }

    setItem(DEFAULT_APP_IN_ORG, updatedData);
};

// Default orgId for userId

export const getDefaultOrgIdForUserId: (userId: string) => string = (userId) => {
    const data: StringKeyedDictionary<string> = getItem(DEFAULT_ORG_FOR_USER);

    return data ? data[userId] : null;
};

export const setDefaultOrgIdForUserId: (userId: string, orgId: string) => void = (userId, orgId) => {
    const data: StringKeyedDictionary<string> = getItem(DEFAULT_ORG_FOR_USER);

    const updatedData = {
        ...data,
        [userId]: orgId,
    };

    setItem(DEFAULT_ORG_FOR_USER, updatedData);
};

//  Setup steps data

interface SetupStepsLocalData {
    hasBeenVisited: boolean;
}

export const getAllSetupStepsLocalData: () => Record<SetupStepType, SetupStepsLocalData> = () => {
    return getItem(SETUP_STEPS_DATA) ?? ({} as Record<SetupStepType, SetupStepsLocalData>);
};

export const getSetupStepLocalData: (appId: string, stepType: SetupStepType) => SetupStepsLocalData = (
    appId,
    stepType,
) => {
    return getAllSetupStepsLocalData()[`${appId}#${stepType}`];
};

export const setSetupStepLocalData: (stepType: SetupStepType, appId: string, data: SetupStepsLocalData) => void = (
    stepType,
    appId,
    data,
) => {
    const previousData = getAllSetupStepsLocalData();
    setItem(SETUP_STEPS_DATA, {
        ...previousData,
        [`${appId}#${stepType}`]: data,
    });
};

//  New automations seen
export const getSeenNewAutomations: () => Partial<Record<AutomationType, true>> = () => {
    return getItem(SEEN_NEW_AUTOMATIONS);
};

export const setSeenNewAutomations: (data: Partial<Record<AutomationType, true>>) => void = (data) => {
    setItem(SEEN_NEW_AUTOMATIONS, data);
};

//  Updated automations seen
export const getSeenUpdatedAutomations: () => Partial<Record<AutomationType, true>> = () => {
    return getItem(SEEN_UPDATED_AUTOMATIONS);
};

export const setSeenUpdatedAutomations: (data: Partial<Record<AutomationType, true>>) => void = (data) => {
    setItem(SEEN_UPDATED_AUTOMATIONS, data);
};

// Get notified for new integrations - saves an array of IntegrationType's to be notified about, keyed off orgId

export const getPreferencesForNotifyingOfComingSoonIntegrations: (orgId: string) => IntegrationType[] = (orgId) => {
    const preferences = getItem(GET_NOTIFIED_COMING_SOON_INTEGRATIONS) ?? {};

    return preferences[orgId] ?? [];
};

export const setPreferencesForNotifyingOfComingSoonIntegrations: (orgId: string, data: IntegrationType[]) => void = (
    orgId,
    data,
) => {
    const currentPrefs: StringKeyedDictionary<IntegrationType[]> = getItem(GET_NOTIFIED_COMING_SOON_INTEGRATIONS) ?? {};

    const updatedPrefs: StringKeyedDictionary<IntegrationType[]> = {
        ...currentPrefs,
        [orgId]: data,
    };

    setItem(GET_NOTIFIED_COMING_SOON_INTEGRATIONS, updatedPrefs);
};

// Dismiss qualitative onboarding

export const getOnboardingDismissedForAppId: (appId: string) => boolean = (appId) => {
    const preferences = getItem(QUALITATIVE_ONBOARDING_DISMISSED) ?? {};

    return preferences[appId] ?? false;
};

export const setOnboardingDismissedForAppId: (appId: string, isDismissed: boolean) => void = (appId, isDismissed) => {
    const currentPrefs: Record<string, boolean> = getItem(QUALITATIVE_ONBOARDING_DISMISSED) ?? {};

    const updatedPrefs: Record<string, boolean> = {
        ...currentPrefs,
        [appId]: isDismissed,
    };

    setItem(QUALITATIVE_ONBOARDING_DISMISSED, updatedPrefs);
};

// Summary preferences

type AppSummaryPreferencesMap = Record<string, Partial<OrgSummaryPreferences>>;

const getAppPreferencesMap = (): AppSummaryPreferencesMap => {
    return getItem<AppSummaryPreferencesMap>(APP_SUMMARY_VIEW_PREFERENCES) ?? {};
};

export const getAppSummaryPreferences = (appId: string): Partial<OrgSummaryPreferences> => {
    const appPrefs = getAppPreferencesMap();
    return appPrefs[appId] ?? {};
};

export const getSummaryViewSearchParams = (appIdParam: string): string => {
    const prefs = getAppSummaryPreferences(appIdParam);
    const params = new URLSearchParams();

    if (prefs.pageView === 'tabular') {
        params.set('pageView', 'tabular');
        if (prefs.tabularLayout) {
            params.set('tabularLayout', prefs.tabularLayout);
        }
    }
    if (prefs.selectedMetrics?.length) {
        params.set('metrics', prefs.selectedMetrics.join(','));
    }
    if (prefs.selectedVersions?.length) {
        params.set('versions', prefs.selectedVersions.join(','));
    }
    if (prefs.selectedDestinationIds?.length) {
        params.set('destinations', prefs.selectedDestinationIds.join(','));
    }

    return params.toString();
};

export const setAppSummaryPreferences = (
    appId: string,
    preferences: Partial<OrgSummaryPreferences>,
): Partial<OrgSummaryPreferences> => {
    const appPrefs = getAppPreferencesMap();
    const currentPrefs = appPrefs[appId] ?? {};
    const updatedPrefs = {
        ...currentPrefs,
        ...preferences,
    };

    setItem(APP_SUMMARY_VIEW_PREFERENCES, {
        ...appPrefs,
        [appId]: updatedPrefs,
    });

    return updatedPrefs;
};

export const getOrgSummaryPreferences = (): Partial<OrgSummaryPreferences> => {
    return getItem<Partial<OrgSummaryPreferences>>(ORG_SUMMARY_VIEW_PREFERENCES) ?? {};
};

export const setOrgSummaryPreferences = (
    preferences: Partial<OrgSummaryPreferences>,
): Partial<OrgSummaryPreferences> => {
    const currentPrefs = getOrgSummaryPreferences();
    const updatedPrefs = {
        ...currentPrefs,
        ...preferences,
    };
    setItem(ORG_SUMMARY_VIEW_PREFERENCES, updatedPrefs);
    return updatedPrefs;
};

export const getSelectedDestinationIds: () => string[] | null = () => {
    const preferences = getOrgSummaryPreferences();
    return preferences.selectedDestinationIds ?? null;
};

export const setSelectedDestinationIds: (ids: string[]) => void = (ids) => {
    setOrgSummaryPreferences({ selectedDestinationIds: ids });
};

// Dismiss register device

export const getRegisterDeviceDismissedForAppId: (appId: string) => boolean = (appId) => {
    const preferences = getItem(REGISTER_DEVICE_DISMISSED) ?? {};

    return preferences[appId] ?? false;
};

export const setRegisterDeviceDismissedForAppId: (appId: string, isDismissed: boolean) => void = (
    appId,
    isDismissed,
) => {
    const currentPrefs: Record<string, boolean> = getItem(REGISTER_DEVICE_DISMISSED) ?? {};

    const updatedPrefs: Record<string, boolean> = {
        ...currentPrefs,
        [appId]: isDismissed,
    };

    setItem(REGISTER_DEVICE_DISMISSED, updatedPrefs);
};

// View / dismiss release feedback survey

export const getUserViewedReleaseSurveyForRelease: (releaseId: string) => boolean = (releaseId) => {
    const releasePilotViewedSurveyForReleaseMap = getItem(RELEASE_FEEDBACK) ?? {};

    return releasePilotViewedSurveyForReleaseMap[releaseId] ?? false;
};

export const setUserViewedReleaseSurveyForRelease: (releaseId: string, didView: boolean) => void = (
    releaseId,
    didView,
) => {
    const releasePilotViewedSurveyForReleaseMap: Record<string, boolean> = getItem(RELEASE_FEEDBACK) ?? {};

    const updatedReleasePilotViewedSurveyForReleaseMap: Record<string, boolean> = {
        ...releasePilotViewedSurveyForReleaseMap,
        [releaseId]: didView,
    };

    setItem(RELEASE_FEEDBACK, updatedReleasePilotViewedSurveyForReleaseMap);
};

// Feature readiness per-app storage helpers

const getPerAppValue = (key: string, appId: string): string => {
    const data = getItem<Record<string, string>>(key);
    if (!data || typeof data !== 'object' || Array.isArray(data)) return '';
    return data[appId] ?? '';
};

const setPerAppValue = (key: string, appId: string, value: string): void => {
    const existing = getItem<Record<string, string>>(key);
    const data: Record<string, string> =
        existing && typeof existing === 'object' && !Array.isArray(existing) ? existing : {};
    data[appId] = value;
    setItem(key, data);
};

const clearPerAppValue = (key: string, appId: string): void => {
    const existing = getItem<Record<string, string>>(key);
    if (!existing || typeof existing !== 'object' || Array.isArray(existing)) return;
    delete existing[appId];
    setItem(key, existing);
};

// Feature readiness items filters

export const getFeatureItemsFiltersStoredParams = (appId: string): string => getPerAppValue(FR_ITEMS_FILTERS, appId);
export const setFeatureItemsFiltersStoredParams = (appId: string, params: string): void =>
    setPerAppValue(FR_ITEMS_FILTERS, appId, params);
export const clearFeatureItemsFiltersStoredParams = (appId: string): void => clearPerAppValue(FR_ITEMS_FILTERS, appId);

// Feature readiness items sort

export const getFeatureItemsSortStoredParams = (appId: string): string => getPerAppValue(FR_ITEMS_SORT, appId);
export const setFeatureItemsSortStoredParams = (appId: string, params: string): void =>
    setPerAppValue(FR_ITEMS_SORT, appId, params);
export const clearFeatureItemsSortStoredParams = (appId: string): void => clearPerAppValue(FR_ITEMS_SORT, appId);

// Feature items only fixes

export const getFeatureItemsOnlyFixesStoredParams = (appId: string): string =>
    getPerAppValue(FT_ITEMS_ONLY_FIXES, appId);
export const setFeatureItemsOnlyFixesStoredParams = (appId: string, params: string): void =>
    setPerAppValue(FT_ITEMS_ONLY_FIXES, appId, params);
export const clearFeatureItemsOnlyFixesStoredParams = (appId: string): void =>
    clearPerAppValue(FT_ITEMS_ONLY_FIXES, appId);

// Feature items group by ticket

export const getFeatureItemsGroupByTicketStoredParams = (appId: string): string =>
    getPerAppValue(FT_ITEMS_GROUP_BY_TICKET, appId);
export const setFeatureItemsGroupByTicketStoredParams = (appId: string, params: string): void =>
    setPerAppValue(FT_ITEMS_GROUP_BY_TICKET, appId, params);
export const clearFeatureItemsGroupByTicketStoredParams = (appId: string): void =>
    clearPerAppValue(FT_ITEMS_GROUP_BY_TICKET, appId);

// Feature items group by epic

export const getFeatureItemsGroupByEpicStoredParams = (appId: string): string =>
    getPerAppValue(FT_ITEMS_GROUP_BY_EPIC, appId);
export const setFeatureItemsGroupByEpicStoredParams = (appId: string, params: string): void =>
    setPerAppValue(FT_ITEMS_GROUP_BY_EPIC, appId, params);
export const clearFeatureItemsGroupByEpicStoredParams = (appId: string): void =>
    clearPerAppValue(FT_ITEMS_GROUP_BY_EPIC, appId);

export const getDependenciesTabVisited: () => boolean = () => {
    return getItem(IOS_DEPENDENCIES_TAB_VISITED) ?? false;
};

export const setDependenciesTabVisited: () => void = () => {
    setItem(IOS_DEPENDENCIES_TAB_VISITED, true);
};

export const getTriageTabVisited: () => boolean = () => {
    return getItem(TRIAGE_TAB_VISITED) ?? false;
};

export const setTriageTabVisited: () => void = () => {
    setItem(TRIAGE_TAB_VISITED, true);
};

// Timeline attention badge: the fingerprint of the failed/blocked automations last seen on a release's timeline

export const getSeenTimelineAttentionFingerprint = (releaseId: string): string =>
    getPerAppValue(SEEN_TIMELINE_ATTENTION, releaseId);
export const setSeenTimelineAttentionFingerprint = (releaseId: string, fingerprint: string): void =>
    setPerAppValue(SEEN_TIMELINE_ATTENTION, releaseId, fingerprint);

// Triage attention badge: when the app's (keyed by appId) or a release's (keyed by releaseId) triage page was last seen

export const getTriageSeenAt = (scopeId: string): string => getPerAppValue(SEEN_TRIAGE_AT, scopeId);
export const setTriageSeenAt = (scopeId: string, seenAt: string): void =>
    setPerAppValue(SEEN_TRIAGE_AT, scopeId, seenAt);

// Dependencies attention badge: the dependency updates seen on any of an app's releases' dependencies page

export const getSeenDependenciesUpdates = (appId: string): string => getPerAppValue(SEEN_DEPENDENCIES_UPDATES, appId);
export const setSeenDependenciesUpdates = (appId: string, seenUpdates: string): void =>
    setPerAppValue(SEEN_DEPENDENCIES_UPDATES, appId, seenUpdates);

// Auth redirect route

const AUTH_REDIRECT_ROUTE_TTL_MS = 30 * 60 * 1000; // 30 minutes

interface AuthRedirectRouteData {
    route: string;
    timestamp: number;
}

// Only allow post-login redirects that stay on the current origin. Resolving the route
// with the browser's URL parser (the same parser navigation uses) rejects protocol-relative
// ("//evil.com"), backslash ("/\evil.com"), and absolute foreign-origin values, while still
// allowing same-origin absolute URLs like the MCP login return URL.
export const isSafeAuthRedirectRoute: (route: string) => boolean = (route) => {
    if (!route) {
        return false;
    }
    try {
        return new URL(route, window.location.origin).origin === window.location.origin;
    } catch {
        return false;
    }
};

export const MCP_LOGIN_PATH = '/mcp/login';

// The auth-flow pages themselves are never valid post-login redirect targets.
// Saving one of these routes would overwrite a legitimate redirect route.
export const isAuthFlowRoute: (route: string) => boolean = (route) => {
    if (!route) {
        return false;
    }
    try {
        const { pathname } = new URL(route, window.location.origin);
        return pathname === '/login' || pathname.startsWith('/login/') || pathname === '/signup';
    } catch {
        return false;
    }
};

// True when the route is the same-origin MCP OAuth return URL, i.e. this login was
// initiated by an MCP client connecting to Runway. Used to tell the backend not to
// auto-create an account for the flow (MCP access requires an existing account).
export const isMCPAuthRedirectRoute: (route: string) => boolean = (route) => {
    if (!route || !isSafeAuthRedirectRoute(route)) {
        return false;
    }
    try {
        return new URL(route, window.location.origin).pathname === MCP_LOGIN_PATH;
    } catch {
        return false;
    }
};

export const getAuthRedirectRoute: () => string = () => {
    const data: AuthRedirectRouteData = getItem(AUTH_REDIRECT_ROUTE);
    console.debug('[REDIRECT DEBUG]', 'getAuthRedirectRoute', data);

    if (!data) {
        return '';
    }

    // Check if the route has expired
    const now = Date.now();
    if (now - data.timestamp > AUTH_REDIRECT_ROUTE_TTL_MS) {
        console.debug('[REDIRECT DEBUG]', 'getAuthRedirectRoute', 'route has expired');
        // Route has expired, clear it
        clearAuthRedirectRoute();
        return '';
    }

    // Re-validate on read so a value stored before validation existed can't trigger
    // an off-origin redirect
    if (!isSafeAuthRedirectRoute(data.route)) {
        console.debug('[REDIRECT DEBUG]', 'getAuthRedirectRoute', 'route is not same-origin, clearing');
        clearAuthRedirectRoute();
        return '';
    }

    // Defense-in-depth: never redirect back to an auth-flow page even if one slipped in.
    if (isAuthFlowRoute(data.route)) {
        console.debug('[REDIRECT DEBUG]', 'getAuthRedirectRoute', 'route is an auth-flow page, clearing');
        clearAuthRedirectRoute();
        return '';
    }

    return data.route;
};

export const setAuthRedirectRoute: (route: string) => void = (route) => {
    console.debug('[REDIRECT DEBUG]', 'setAuthRedirectRoute', route);
    if (!isSafeAuthRedirectRoute(route)) {
        console.debug('[REDIRECT DEBUG]', 'setAuthRedirectRoute', 'route is not same-origin, ignoring');
        return;
    }
    // Never store an auth-flow page as the redirect target — it is not a real destination and
    // would overwrite a legitimately saved route (e.g. the MCP OAuth return URL).
    if (isAuthFlowRoute(route)) {
        console.debug('[REDIRECT DEBUG]', 'setAuthRedirectRoute', 'route is an auth-flow page, ignoring');
        return;
    }
    const data: AuthRedirectRouteData = {
        route,
        timestamp: Date.now(),
    };
    setItem(AUTH_REDIRECT_ROUTE, data);
};

export const clearAuthRedirectRoute: () => void = () => {
    deleteItem(AUTH_REDIRECT_ROUTE);
};

// Helper function to check and redirect to saved route if it exists
export const checkAndRedirectToSavedRoute: () => void = () => {
    console.debug('[REDIRECT DEBUG]', 'checkAndRedirectToSavedRoute');
    const savedRoute = getAuthRedirectRoute();
    if (savedRoute) {
        console.debug('[REDIRECT DEBUG]', 'checkAndRedirectToSavedRoute', 'redirecting to', savedRoute);
        clearAuthRedirectRoute();
        window.location.replace(savedRoute);
    }
};
